import React, { useState, useEffect } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MessageTextProps {
  text: string;
  isUser: boolean;
  isStreaming?: boolean;
}

export const MessageText: React.FC<MessageTextProps> = ({
  text,
  isUser,
  isStreaming = false,
}) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    if (!isStreaming || isUser) {
      setDisplayedText(text);
      return;
    }

    if (displayedText.length >= text.length) return;

    const timer = setTimeout(() => {
      setDisplayedText(text.substring(0, displayedText.length + 2));
    }, 20);

    return () => clearTimeout(timer);
  }, [text, displayedText, isStreaming, isUser]);

  const textToRender = isStreaming ? displayedText : text;
  const components: Components = {
    a({ href, children, ...props }) {
      const isDownload = href?.match(/^https:\/\/wlc360ai/);

      const baseClasses = cn(
        'underline hover:no-underline transition-all',
        isUser
          ? 'text-white'
          : isDownload
          ? 'text-[#007CA0] hover:text-[#005F7D]'
          : 'text-blue-600 hover:text-blue-800',
      );

      if (isDownload) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={baseClasses}
            {...props}>
            download here
          </a>
        );
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClasses}
          {...props}>
          {children}
        </a>
      );
    },

    code({ inline, className, children, ...props }: any) {
      const codeText = String(children).replace(/\n$/, '');

      if (!inline) {
        return (
          <pre
            className={cn(
              'mt-2 w-full overflow-x-auto rounded-md p-3 text-sm font-mono',
              isUser
                ? 'bg-white/10 text-white'
                : 'bg-gray-900 text-gray-100 dark:bg-gray-900 dark:text-gray-100',
            )}>
            <code className={className} {...props}>
              {codeText}
            </code>
          </pre>
        );
      }

      return (
        <code
          className={cn(
            'px-1.5 py-0.5 rounded text-sm font-mono break-words',
            isUser
              ? 'bg-white/20 text-white'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
          )}
          {...props}>
          {children}
        </code>
      );
    },

    table({ children, ...props }) {
      return (
        <div className="overflow-x-auto my-4">
          <table
            className="min-w-full border border-gray-300 divide-y divide-gray-300 dark:border-gray-600 dark:divide-gray-600"
            {...props}>
            {children}
          </table>
        </div>
      );
    },

    thead({ children, ...props }) {
      return (
        <thead className="bg-gray-50 dark:bg-gray-700" {...props}>
          {children}
        </thead>
      );
    },

    th({ children, ...props }) {
      return (
        <th
          className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0"
          {...props}>
          {children}
        </th>
      );
    },

    td({ children, ...props }) {
      return (
        <td
          className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0"
          {...props}>
          {children}
        </td>
      );
    },

    tr({ children, ...props }) {
      return (
        <tr
          className="border-b border-gray-200 dark:border-gray-600 last:border-b-0"
          {...props}>
          {children}
        </tr>
      );
    },

    ul({ children, ...props }) {
      return (
        <ul
          className="list-disc space-y-1 my-2 first:mt-0 last:mb-0 pl-5 [&_ul]:my-1 [&_ol]:my-1"
          {...props}>
          {children}
        </ul>
      );
    },

    ol({ children, ...props }) {
      return (
        <ol
          className="list-decimal space-y-1 my-2 first:mt-0 last:mb-0 pl-5 [&_ul]:my-1 [&_ol]:my-1"
          {...props}>
          {children}
        </ol>
      );
    },

    li({ children, ...props }) {
      return (
        <li className="leading-normal pl-1 [&>p]:my-0 [&>p]:inline" {...props}>
          {children}
        </li>
      );
    },

    p({ children, ...props }) {
      return (
        <p
          className={cn(
            'leading-relaxed first:mt-0 last:mb-0',
            isUser ? 'my-1' : 'my-2',
          )}
          {...props}>
          {children}
        </p>
      );
    },

    h1({ children, ...props }) {
      return (
        <h1 className="text-2xl font-bold mt-4 mb-2 first:mt-0" {...props}>
          {children}
        </h1>
      );
    },

    h2({ children, ...props }) {
      return (
        <h2 className="text-xl font-bold mt-3 mb-2 first:mt-0" {...props}>
          {children}
        </h2>
      );
    },

    h3({ children, ...props }) {
      return (
        <h3 className="text-lg font-semibold mt-3 mb-1.5 first:mt-0" {...props}>
          {children}
        </h3>
      );
    },

    blockquote({ children, ...props }) {
      return (
        <blockquote
          className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-2 text-sm text-gray-700 dark:text-gray-200"
          {...props}>
          {children}
        </blockquote>
      );
    },

    strong({ children, ...props }) {
      return (
        <strong className="font-bold" {...props}>
          {children}
        </strong>
      );
    },

    em({ children, ...props }) {
      return (
        <em className="italic" {...props}>
          {children}
        </em>
      );
    },
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
      {textToRender}
    </ReactMarkdown>
  );
};
